import { useRef, useEffect, useState } from "react";
import { DayColumn } from "./DayColumn";
import { PlannerTaskBar } from "./PlannerTaskBar";
import { DayCapacity, PlannerTask, PlannerPeriod } from "./plannerUtils";
import { cn } from "@/lib/utils";

interface PlannerGridProps {
  periodDays: Date[];
  dayCapacities: DayCapacity[];
  plannerTasks: PlannerTask[];
  period: PlannerPeriod;
  onTaskClick?: (task: PlannerTask) => void;
}

export function PlannerGrid({
  periodDays,
  dayCapacities,
  plannerTasks,
  period,
  onTaskClick,
}: PlannerGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dayWidth, setDayWidth] = useState(60);
  const [needsScroll, setNeedsScroll] = useState(false);

  // Calculate day width based on container and period
  useEffect(() => {
    const updateWidth = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.clientWidth;
      const daysCount = periodDays.length;

      if (period === 'week') {
        setDayWidth(Math.floor(containerWidth / daysCount));
        setNeedsScroll(false);
      } else if (period === '2-weeks') {
        const minWidth = 80;
        const calculatedWidth = Math.floor(containerWidth / daysCount);
        setDayWidth(Math.max(minWidth, calculatedWidth));
        setNeedsScroll(calculatedWidth < minWidth);
      } else {
        const minWidth = 50;
        const calculatedWidth = Math.floor(containerWidth / daysCount);
        setDayWidth(Math.max(minWidth, calculatedWidth));
        setNeedsScroll(true);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [periodDays.length, period]);

  // Calculate grid height based on max row
  const maxRow = plannerTasks.reduce((max, t) => Math.max(max, t.row), -1);
  const gridHeight = Math.max(200, (maxRow + 1) * 60 + 20);

  const isCompact = period === 'month';
  const totalWidth = periodDays.length * dayWidth;

  return (
    <div
      ref={containerRef}
      className="border rounded-lg bg-card w-full min-w-0"
      style={{ overflow: 'hidden', contain: 'inline-size' }}
    >
      {/* This div is the ONLY horizontal scroll container */}
      <div
        className="overflow-x-auto overflow-y-hidden pb-2"
      >
        {/* Wide content lives inside the scroll container */}
        <div
          style={{ width: needsScroll ? totalWidth : '100%' }}
        >
          {/* Day headers */}
          <div className="flex border-b bg-muted/30">
            {dayCapacities.map((capacity, index) => (
              <div
                key={index}
                style={{ width: needsScroll ? dayWidth : undefined }}
                className={cn(!needsScroll && "flex-1")}
              >
                <DayColumn capacity={capacity} isCompact={isCompact} />
              </div>
            ))}
          </div>

          {/* Task grid */}
          <div
            className="relative"
            style={{ height: gridHeight }}
          >
            {/* Grid lines */}
            <div className="absolute inset-0 flex pointer-events-none">
              {periodDays.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "border-r border-border/50 last:border-r-0",
                    !dayCapacities[index]?.isWorkingDay && "bg-muted/20"
                  )}
                  style={{ width: needsScroll ? dayWidth : undefined, flex: needsScroll ? undefined : 1 }}
                />
              ))}
            </div>

            {/* Task bars */}
            {plannerTasks.map((task) => (
              <PlannerTaskBar
                key={task.id}
                task={task}
                onClick={onTaskClick}
                dayWidth={dayWidth}
                dayCapacities={dayCapacities}
              />
            ))}

            {/* Empty state */}
            {plannerTasks.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                <p className="text-sm">No tasks in this period</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
